import { createContext, useCallback, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import {
  REFRESH_MARGIN_MS, completeSignInFromUrl, hasPendingSignIn, hydrateSession, initialAuthStatus,
  isAuthConfigured, readSession, refreshSession, sendMagicLink, signOut as clearSession,
  type AuthSession, type AuthStatus,
} from '@/services/auth.service';
import {
  BillingError, FAIL_CLOSED_CODES, FREE_ENTITLEMENT, fetchEntitlement,
  type Entitlement,
} from '@/services/billing.client';
import { useSettings } from '@/hooks/useSettings';
import { useToast } from '@/hooks/useToast';

export interface AccountContextValue {
  /**
   * Whether anyone is signed in — decided by Supabase, never by the plan.
   * `restoring` while a stored session is being confirmed: the UI shows neither
   * the signed-in nor the signed-out state until it resolves.
   */
  authStatus: AuthStatus;
  session: AuthSession | null;
  entitlement: Entitlement | null;
  isConfigured: boolean;
  isBusy: boolean;
  signIn: (email: string) => Promise<boolean>;
  signOut: () => void;
  refresh: () => Promise<void>;
}

export const AccountContext = createContext<AccountContextValue | null>(null);

/** How soon to try renewing again when Supabase could not be reached. */
const RENEW_RETRY_MS = 30_000;

/**
 * Identity and paid entitlement.
 *
 * Entitlement is *asked for*, never asserted. Whatever is in local storage is a
 * cache of a server answer, and the server recomputes it on every request — so
 * a lapsed subscription downgrades on the next refresh without anything having
 * to expire locally.
 *
 * The whole provider is inert when Supabase is not configured, which keeps the
 * app working exactly as it did before payments existed.
 *
 * Identity and plan are separate answers. `authStatus` comes from Supabase and
 * decides the account UI; `entitlement` comes from the subscription endpoint and
 * decides the plan. Neither stands in for the other.
 */
export function AccountProvider({ children }: PropsWithChildren) {
  const configured = isAuthConfigured();
  const { notify } = useToast();
  const { settings, update: updateSettings } = useSettings();

  const [session, setSession] = useState<AuthSession | null>(null);
  // Decided synchronously from what is on this device, so a genuinely signed-out
  // visitor sees the sign-in form at once and a signed-in one never does.
  const [authStatus, setAuthStatus] = useState<AuthStatus>(
    () => initialAuthStatus(configured, hasPendingSignIn(), readSession()),
  );
  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const hydrated = useRef(false);

  // Restore a stored session, or finish one arriving from a magic link. Runs on
  // every full page load — first visit, refresh, a deep link to Settings, and
  // the return from hosted checkout alike.
  useEffect(() => {
    if (!configured || hydrated.current) return;
    hydrated.current = true;

    const candidate = completeSignInFromUrl() ?? readSession();
    if (!candidate) {
      setAuthStatus('signed-out');
      return;
    }

    void hydrateSession(candidate).then((result) => {
      if (result.status === 'confirmed') {
        setSession(result.session);
        setAuthStatus('signed-in');
      } else if (result.status === 'unreachable' && candidate.userId !== 'pending') {
        // Supabase gave no answer, so nothing was decided. This session was
        // confirmed by Supabase when it was stored; show it rather than a
        // sign-in form. It grants nothing here — every API call is verified
        // server-side, and the renewal below retries until Supabase answers.
        setSession(candidate);
        setAuthStatus('signed-in');
      } else {
        setAuthStatus('signed-out');
      }
    });
  }, [configured]);

  /**
   * Keeps the session alive while the page is open.
   *
   * The access token expires after an hour; renewing it shortly before then
   * means checkout, the portal and the status call never go out with a dead
   * token. A refusal signs out, because Supabase has said the session is over.
   * No answer is not a refusal, so it is simply tried again.
   */
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    let timer: number | undefined;

    const renew = () => {
      // Another tab may have renewed first. Refresh tokens are single-use, and
      // spending one that was already rotated looks like theft to Supabase —
      // which revokes the whole session. So the newest stored session wins.
      const stored = readSession();
      const current = stored && stored.userId === session.userId && stored.expiresAt > session.expiresAt
        ? stored : session;
      if (current !== session && current.expiresAt - REFRESH_MARGIN_MS > Date.now()) {
        setSession(current);
        return;
      }
      void refreshSession(current).then((result) => {
        if (cancelled) return;
        if (result.status === 'confirmed') {
          setSession(result.session);
        } else if (result.status === 'rejected') {
          setSession(null);
          setAuthStatus('signed-out');
        } else {
          timer = window.setTimeout(renew, RENEW_RETRY_MS);
        }
      });
    };

    timer = window.setTimeout(renew, Math.max(session.expiresAt - REFRESH_MARGIN_MS - Date.now(), 0));
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [session]);

  /**
   * Ask the server what this session is entitled to.
   *
   * Two failure modes, deliberately treated differently:
   *
   *   - The server *answers* that it will not vouch for this session
   *     (401/403). That is information, and the honest response is to fall
   *     back to free immediately rather than keep serving a stale Pro answer
   *     from an expired or revoked session.
   *   - The request never got an answer — offline, DNS, a 5xx. That is not
   *     information about entitlement, so the last known answer stands and the
   *     next refresh settles it. This path can only ever *keep* a plan; it can
   *     never grant one, because it never writes a value.
   */
  const refresh = useCallback(async () => {
    if (!session) { setEntitlement(null); return; }
    try {
      setEntitlement(await fetchEntitlement(session));
    } catch (error) {
      if (error instanceof BillingError && FAIL_CLOSED_CODES.has(error.code)) {
        setEntitlement(FREE_ENTITLEMENT);
      }
    }
  }, [session]);

  useEffect(() => { void refresh(); }, [refresh]);

  /**
   * Mirrors the server's answer into settings, which is what `withLimit()` and
   * the PDF branding already read.
   *
   * The stored value is a *cache*, not a source of truth: it is overwritten by
   * the server on every refresh, and there is no longer any UI that can set it.
   * Editing it by hand in devtools buys Pro until the next status call — see
   * PHASE5.md, which is honest that real enforcement needs server-held
   * resources, and QuotyLab's quotes are deliberately local.
   */
  useEffect(() => {
    if (!configured || !settings || !entitlement) return;
    if (settings.plan === entitlement.plan) return;
    void updateSettings({ plan: entitlement.plan });
  }, [configured, settings, entitlement, updateSettings]);

  /**
   * No session, no Pro.
   *
   * The cached plan is a server answer about a signed-in account. Once there is
   * genuinely no session — signed out, or refused by Supabase — nobody is left
   * for that answer to be about, and leaving it at Pro is how the sidebar said
   * "Pro plan" beside a sign-in form. It resets only on a settled signed-out:
   * never while restoring, and never because Supabase was unreachable.
   */
  useEffect(() => {
    if (!configured || authStatus !== 'signed-out' || !settings) return;
    if (settings.plan === 'free') return;
    void updateSettings({ plan: 'free' });
  }, [configured, authStatus, settings, updateSettings]);

  const signIn = useCallback(async (email: string): Promise<boolean> => {
    setIsBusy(true);
    try {
      await sendMagicLink(email);
      return true;
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Could not send the link.', 'error');
      return false;
    } finally {
      setIsBusy(false);
    }
  }, [notify]);

  const signOut = useCallback(() => {
    clearSession(session);
    setSession(null);
    setAuthStatus('signed-out');
    setEntitlement(null);
    notify('Signed out.');
  }, [notify, session]);

  const value = useMemo<AccountContextValue>(
    () => ({ authStatus, session, entitlement, isConfigured: configured, isBusy, signIn, signOut, refresh }),
    [authStatus, session, entitlement, configured, isBusy, signIn, signOut, refresh],
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}
